/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { ArrowLeft, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const openPrefs = () => {
  window.dispatchEvent(new CustomEvent("finx-open-cookie-preferences"));
};

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-xl md:text-2xl font-black text-slate-900 mb-3">
      <span className="text-[#5B21B6] mr-2">{n}.</span>{title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-display font-bold text-slate-900 mt-5 mb-2">{children}</h3>
);

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <Link to="/" data-testid="cookie-policy-back" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#5B21B6] mb-8">
          <ArrowLeft size={16} /> Back to FinX
        </Link>

        <div className="inline-flex items-center gap-2 rounded-full bg-[#EDE9FE] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#5B21B6] mb-5">
          <Cookie size={14} /> Cookie Policy
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-900 mb-4">
          FinX <span className="brand-gradient-text">Cookie Policy</span>
        </h1>
        <p className="text-slate-500 text-sm">Last updated: July 21, 2026</p>

        <div className="mt-10 text-slate-700 leading-relaxed space-y-4">
          <p>
            This Cookie Policy explains how <b>Golden State Tech Inc.</b> ("FinX," "we," "us," or "our") uses cookies and similar technologies on finxstocks.com and related FinX websites, products, and services.
          </p>
          <p>
            This policy should be read together with our <Link to="/legal/privacy.pdf" target="_blank" className="text-[#5B21B6] font-semibold underline">Privacy Policy</Link> and <Link to="/legal/terms.pdf" target="_blank" className="text-[#5B21B6] font-semibold underline">Terms of Use</Link>.
          </p>
        </div>

        <div className="mt-12">
          <Section n="1" title="What Cookies Are">
            <p>Cookies are small text files placed on your browser or device when you visit a website. They help websites remember information about your visit, such as your preferences, device, browser, and activity on the site.</p>
            <p>We may also use similar technologies, such as pixels, tags, SDKs, local storage, and device identifiers. In this policy, we refer to these technologies together as "cookies."</p>
          </Section>

          <Section n="2" title="How We Use Cookies">
            <SubHeading>Strictly Necessary Cookies</SubHeading>
            <p>These cookies are required for our website and services to work properly. They may support security, fraud prevention, network management, page navigation, form submission, and remembering your privacy choices.</p>
            <p>You cannot disable strictly necessary cookies through our cookie preference tools, although you may be able to block them in your browser. If you block them, parts of the site may not work correctly.</p>

            <SubHeading>Functional Cookies</SubHeading>
            <p>These cookies help us remember your preferences and improve your experience, such as language, region, display settings, or previously entered information.</p>

            <SubHeading>Analytics and Performance Cookies</SubHeading>
            <p>These cookies help us understand how visitors use our website and services, so we can improve performance, features, and the user experience. This may include information about pages visited, time on site, and general device or browser information.</p>

            <SubHeading>Marketing Cookies</SubHeading>
            <p>Where applicable, these cookies help measure the effectiveness of our marketing efforts and may support more relevant content or advertising related to FinX and financial education. We do not use marketing cookies to make automated decisions with legal or similarly significant effects about you.</p>
          </Section>

          <Section n="3" title="Third-Party Cookies">
            <p>Some cookies on our website may be set by third-party service providers we use to operate, secure, analyze, or improve our services, or to support communications. Third-party providers may include, for example, hosting and infrastructure providers, analytics providers, email and communication services, security and fraud-prevention providers, and, in the future, market data or payment-related providers.</p>
            <p>These third parties may collect information about your online activities across websites and over time. Their use of cookies is governed by their own privacy notices.</p>
          </Section>

          <Section n="4" title="Your Choices">
            <SubHeading>Cookie Banner and Preference Center</SubHeading>
            <p>When you first visit our website, we may present a cookie banner that allows you to accept or reject non-essential cookies, and to manage your preferences by category. You can change your preferences at any time by clicking the cookie preferences link in the footer.</p>
            <div className="my-4">
              <Button data-testid="cookie-policy-open-prefs" onClick={openPrefs} className="rounded-full brand-gradient text-white font-bold border-0">
                <Cookie size={14} className="mr-2" /> Manage Cookie Preferences
              </Button>
            </div>

            <SubHeading>Browser Settings</SubHeading>
            <p>Most browsers let you view, delete, and block cookies. You can usually find these controls in your browser's settings or help menu. Blocking or deleting cookies may affect how our website functions.</p>

            <SubHeading>Do Not Track</SubHeading>
            <p>Some browsers offer a "Do Not Track" signal. Because there is no consistent industry standard for how to respond to these signals, our website may not respond to them at this time. We will update this policy if that changes.</p>
          </Section>

          <Section n="5" title="Data Collected Through Cookies">
            <p>Information collected through cookies may include, for example: IP address and general location, device and browser information, pages you visit and interactions on our website, referring URLs, and information about how you use our services and features.</p>
            <p>Where this information is combined with information that identifies you, we treat it as personal information under our Privacy Policy.</p>
          </Section>

          <Section n="6" title="International Users">
            <p>FinX may be accessed by users in various jurisdictions. Where local law requires additional cookie disclosures or consent flows (for example, in parts of the European Economic Area, the United Kingdom, Brazil, or certain U.S. states), we will provide those disclosures and controls in accordance with applicable law.</p>
          </Section>

          <Section n="7" title="Changes to This Policy">
            <p>We may update this Cookie Policy from time to time. When we do, we will update the "Last updated" date above and may take additional steps required by law (for example, showing a renewed cookie banner where required).</p>
          </Section>

          <Section n="8" title="Contact">
            <p>If you have questions about this Cookie Policy or our use of cookies, please contact us at <a href="mailto:info@finxstocks.com" className="text-[#5B21B6] font-semibold">info@finxstocks.com</a>.</p>
            <p className="text-sm text-slate-500 mt-6 pt-6 border-t border-slate-100">
              This Cookie Policy is provided for general information. It does not constitute legal advice.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-100">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#5B21B6]">
            <ArrowLeft size={16} /> Back to FinX
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
